/* @flow */
import { Record, type Map } from 'immutable';
import { toUpper } from 'trustar/utils';
import { pipe } from 'ramda';

export const INCIDENT_REPORT_TYPE = 'INCIDENT_REPORT';
export const INDICATOR_TAG = 'INDICATOR_TAG';
export const INDICATOR_TYPE = 'INDICATOR';
export const INDICATOR_TYPES = {
  IP: 'IP',
  MD5: 'MD5',
  SHA1: 'SHA1',
  SHA256: 'SHA256',
  DOMAIN: 'DOMAIN',
  FQDN: 'FQDN',
  URL: 'URL',
  CVE: 'CVE',
  EMAIL_ADDRESS: 'EMAIL_ADDRESS',
  LOCATION: 'LOCATION',
  SOFTWARE: 'SOFTWARE',
  FILE: 'FILE',
  PERSON: 'PERSON',
  ORGANIZATION: 'ORGANIZATION',
  MALWARE: 'MALWARE',
  REGISTRY_KEY: 'REGISTRY_KEY',
  DATE: 'DATE',
  BITCOIN_ADDRESS: 'BITCOIN_ADRESS',
  CIDR_BLOCK: 'CIDR_BLOCK',
  PHONE_NUMBER: 'PHONE_NUMBER',
  X_ID: 'X_ID',
  THREAT_ACTOR: 'THREAT_ACTOR',
};

export type NodeType =
    typeof INCIDENT_REPORT_TYPE
  | typeof INDICATOR_TYPES.IP
  | typeof INDICATOR_TYPES.URL
  | typeof INDICATOR_TYPES.MD5
  | typeof INDICATOR_TYPES.SHA1
  | typeof INDICATOR_TYPES.SHA256
  | typeof INDICATOR_TYPES.CVE
  | typeof INDICATOR_TYPES.SOFTWARE
  | typeof INDICATOR_TYPES.EMAIL_ADDRESS
  | typeof INDICATOR_TYPES.MALWARE
  | typeof INDICATOR_TYPES.REGISTRY_KEY
  | typeof INDICATOR_TYPES.DATE
  | typeof INDICATOR_TYPES.BITCOIN_ADDRESS
  | typeof INDICATOR_TYPES.CIDR_BLOCK
  | typeof INDICATOR_TYPES.PHONE_NUMBER
  | typeof INDICATOR_TYPES.X_ID
  | typeof INDICATOR_TYPES.THREAT_ACTOR;

export type ForceEvent = {
  type: 'start' | 'tick' | 'end',
  alpha: number,
};

export type ForceCacheShape = {
  nodes: { [key: string]: ForceNode },
  edges: { [key: string]: ForceSimulationEdge },
};

export const convertIndicatorTypeToLabel = (indicatorType: ?string) => {
  switch (indicatorType) {
  case INDICATOR_TYPES.IP:
    return 'IP';
  case INDICATOR_TYPES.EMAIL_ADDRESS:
    return 'Email Address';
  case INDICATOR_TYPES.DOMAIN:
    return 'Domain';
  case INDICATOR_TYPES.SOFTWARE:
    return 'Software';
  case INDICATOR_TYPES.LOCATION:
    return 'Location';
  case INDICATOR_TYPES.FILE:
    return 'File';
  case INDICATOR_TYPES.PERSON:
    return 'Person';
  case INDICATOR_TYPES.ORGANIZATION:
    return 'Organization';
  case INDICATOR_TYPES.REGISTRY_KEY:
    return 'Registry Key';
  case INDICATOR_TYPES.DATE:
    return 'Date';
  case INDICATOR_TYPES.BITCOIN_ADDRESS:
    return 'Bitcoin Address';
  case INDICATOR_TYPES.CIDR_BLOCK:
    return 'CIDR Block';
  case INDICATOR_TYPES.PHONE_NUMBER:
    return 'Phone Number';
  case INDICATOR_TYPES.X_ID:
    return 'X ID';
  case INDICATOR_TYPES.THREAT_ACTOR:
    return 'THREAT_ACTOR';
  default:
    return indicatorType;
  }
};

/**
 * node -> display label for node
 */
export const getIndicatorLabel = pipe(x => x.get('type'), convertIndicatorTypeToLabel);

/**
 * Handle some custom uppwercasing logic around CVEs
 */
export const getIndicatorDisplayValue = (node: Map<string, string>): string => {
  const label = getIndicatorLabel(node);
  const value = node.get('value', '');
  return label === 'CVE' ? toUpper(value) : value;
};

export const Note = Record({
  guid: '', // The id of this record
  nodeGuid: '', // The id of the graph node this note relates to
  userGuid: '', // The id of the user who authored this note
  created: -1, // Created timestamp
  updated: -1, // Updated timestamp
  note: '', // The raw, stringified JSON content state (result fo NoteIso.to)
  editorState: null, // The editor state, which only ever exists client side and is sent into the draft editor
});

export type RawNoteResponse = {
  created: number,
  guid: string,
  nodeGuid: string,
  note: string,
  updated: number,
  userGuid: string,
};



// WEBPACK FOOTER //
// ./web_modules/trustar/constellation/types.js